import {Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {MatDialog, MatDialogModule} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatInputModule} from '@angular/material/input';
import {MatButtonModule} from '@angular/material/button';
import {UserService} from '../../data-access-layer/services/users.service';
import { Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { TranslocoModule, TRANSLOCO_CONFIG, translocoConfig, TRANSLOCO_LOADER } from '@ngneat/transloco';
import { SourceService } from '../../data-access-layer/services/sources.service';
import { AlertService } from '../../data-access-layer/services/alert.service';
import { Agent, Driver } from '../../data-access-layer/interfaces/source.interface';
import { PythonService } from '../../data-access-layer/services/python.service';
import { LoadingService } from '../../data-access-layer/services/loading.service';
import { firstValueFrom } from 'rxjs';
import type { Options } from 'flatpickr/dist/types/options';


type AgentStatusKey = 'missing' | 'running' | 'stopped' | 'deleted';

type DagStats = {
  queued: number;
  running: number;
  success: number;
  failed: number;
};

interface AgentsTableRow {
  agentId?: number;
  type: string;
  scheduleText: string;
  statusKey: AgentStatusKey;
  hasAgent: boolean;
  dagId?: string;
  isDagRunning?: boolean;
  isDagLoading?: boolean;
  dagStats?: DagStats;
  isDagStatsLoading?: boolean;
  name?: string;
  displayName: string;
  isManualOnly: boolean;
  isStopped?: boolean;
  isDeleted?: boolean;
}


@Component({
  selector: 'source-settings-dialog',
  standalone: false,
  templateUrl: '../templates/source-settings.component.html',
  styleUrls: ['../templates/styles/sources-settings.component.css']
})
export class SourceDialogComponent implements OnInit, OnChanges {

  @Input() isVisible = true;
  @Input() isDataCatalog = false;
  @Input() isAgent = false;

  @Input() isNewSource = false;
  @Input() isShowBI = false;

  @Input() selectedSource: any = {}
  @Output() isVisibleChange = new EventEmitter<boolean>();

  @Output() getSources = new EventEmitter<any>();


  public typesDC: string[] = [
    "OpenMetadata",
    "DataHub"
  ];

  public typesAgent: string[] = ["extract_meta", "analyze"];

  open = false;
  public agentsTable: AgentsTableRow[] = [];
  public isAgentsTableLoading = false;
  public isAgentEditorVisible = false;
  public isAddAgentMenuOpen = false;

  config = { enableTime: true, dateFormat: 'Y-m-d H:i' };

  public driversDWH: Driver[] = [
    { driverName: "MS SQL", defaultPort: "1433" },
    { driverName: "PostgreSQL", defaultPort: "5432" },
    { driverName: "ClickHouse", defaultPort: "8123" },
    { driverName: "Greenplum", defaultPort: "5432" },
  ];

  public typesBI: Driver[] = [
    { driverName: "Analytic Workspace", defaultPort: "" },
    { driverName: "Superset", defaultPort: "8088" },
    { driverName: "Power BI", defaultPort: "14033" },
    { driverName: "PixBI", defaultPort: "5432" },
    { driverName: "AlphaBI", defaultPort: "5432" },
    { driverName: "SSRS", defaultPort: "5432" },
    { driverName: "DeltaBI", defaultPort: "5432" },
  ];

  public intervals: string[] = ['hour', 'day', 'week', 'month'];
  public minutes: number[] = Array.from({ length: 60 }, (_, index) => index);
  public hours: number[] = Array.from({ length: 24 }, (_, index) => index);
  public days: string[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  public dates: number[] = Array.from({ length: 31 }, (_, index) => index + 1);

  public selectedAgentType: string = this.typesAgent[0];
  public selectedAgentId: number | null = null;
  public selectedDataCatalog: string = this.typesDC[0];
  public selectedScheduleType: string = this.intervals[0];
  public selectedScheduleDay: string = "";
  public selectedScheduleDate: string = "";
  public agent: any = {
       hour: 0,
       minute: 0,
       weekdayName: 'mon',
       monthday: 1,
       dagId: '',
       scheduleSettings: null,
       name: '',
       userDateTime: null,
       userDateTimeStr: '',
       isStopped: false,
       isDeleted: false
  };


  public isNewAgent: boolean = false;
  public isNoSchedule: boolean = false;
  public isOpenHours: boolean = false;
  public isOpenMins: boolean = false;
  private agentCache = new Map<string, Agent[]>();
  /** ID источника, созданного только для проверки подключения (не через кнопку «Сохранить»). При закрытии диалога — предложить удалить. */
  private createdOnlyForCheckId: number | null = null;
  /** Состояние источника до изменений (для отката при неудачной проверке подключения при редактировании). */
  private initialSourceState: any = null;

  fpOptions: Options = {
    enableTime: true,
    time_24hr: true,
    dateFormat: 'Y-m-d H:i',
    altInput: true,
    altFormat: 'd.m.Y H:i',
    allowInput: true
  };

  constructor(private sourceService: SourceService, private dialog: MatDialog, private router: Router, public transloco: TranslocoService, private pythonService: PythonService, private loadingService: LoadingService, private alertService: AlertService) {
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.isAddAgentMenuOpen) {
      return;
    }
    const target = event.target as HTMLElement;
    if (!target.closest('.add-agent-menu')) {
      this.isAddAgentMenuOpen = false;
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedSource'] && this.selectedSource?.id != null) {
      this.initialSourceState = JSON.parse(JSON.stringify(this.selectedSource));
    }
    if (changes['selectedSource'] && !this.selectedSource?.id) {
      this.initialSourceState = null;
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.selectedSource?.id != null) {
      this.initialSourceState = JSON.parse(JSON.stringify(this.selectedSource));
    }
    this.typesAgent = this.isShowBI ? ["extract_meta", "crawl_images", "analyze", "summarize_metrics", "lineage_report", "full_update"] : ["extract_meta", "analyze", "lineage_dwh", "full_update"];

    if (this.isAgent) {
      await this.initializeAgentsTab();
    }
  }

  private async initializeAgentsTab(): Promise<void> {
    this.agentCache.clear();
    this.isAgentEditorVisible = false;
    await this.loadAgentsTable();
  }

  public toggleAddAgentMenu(event?: Event): void {
    event?.stopPropagation();
    this.isAddAgentMenuOpen = !this.isAddAgentMenuOpen;
  }

  public startNewAgent(agentType: string, event?: Event): void {
    event?.stopPropagation();
    this.isAddAgentMenuOpen = false;
    this.isAgent = true;
    this.isAgentEditorVisible = true;
    this.isNewAgent = true;
    this.selectedAgentType = agentType;
    this.selectedAgentId = null;
    this.isNoSchedule = true;
    this.selectedScheduleType = 'hour';

    const dataSourceType = this.isShowBI ? 'report' : 'dwh';
    const fallbackName = this.transloco.translate('sources.typesAgents.' + agentType);

    this.agent = {
      id: 0,
      dataSourceId: this.selectedSource.id,
      dataSourceType,
      agentType,
      dagId: '',
      hour: 0,
      minute: 0,
      weekdayName: this.days[0],
      monthday: 1,
      scheduleSettings: null,
      isStopped: 1,
      isDeleted: 0,
      userDateTimeStr: '',
      userDateTime: null,
      name: fallbackName
    };
  }

  private async loadAgentsTable(): Promise<void> {
    this.isAgentsTableLoading = true;
    const rows: AgentsTableRow[] = [];

    try {
      for (const agentType of this.typesAgent) {
        const agents = await this.getAgentsForType(agentType, true);
        agents
          .filter(agent => !agent.isDeleted)
          .forEach(agent => rows.push(this.buildAgentsTableRow(agent)));
      }

      this.agentsTable = rows;
      const dagRows = rows.filter(row => !!row.dagId);
      await Promise.all(dagRows.map(row => this.refreshDagState(row.type, row.dagId!, row.agentId)));
      await Promise.all(dagRows.map(row => this.refreshDagStats(row.type, row.dagId!, row.agentId)));
    } finally {
      this.isAgentsTableLoading = false;
    }
  }

  private async getAgentsForType(agentType: string, forceRefresh = false): Promise<Agent[]> {
    if (!forceRefresh && this.agentCache.has(agentType)) {
      return this.cloneAgents(this.agentCache.get(agentType)!);
    }

    const request = {
      agentType: agentType,
      dataSourceId: this.selectedSource.id,
      dataSourceType: this.isShowBI ? 'report' : 'dwh'
    };

    try {
      const response = await firstValueFrom(this.sourceService.getAgent(request));
      const agents = response ?? [];
      this.agentCache.set(agentType, this.cloneAgents(agents));
      return this.cloneAgents(agents);
    } catch (error) {
      console.warn('Не удалось получить агента источника', agentType, error);
      this.agentCache.set(agentType, []);
      return [];
    }
  }

  private async getAgentById(agentType: string, agentId?: number, forceRefresh = false): Promise<Agent | null> {
    const agents = await this.getAgentsForType(agentType, forceRefresh);

    if (typeof agentId === 'number') {
      const match = agents.find(agent => agent.id === agentId);
      return match ? this.cloneAgent(match) : null;
    }

    return agents.length ? this.cloneAgent(agents[0]) : null;
  }

  private cloneAgent(agent: Agent): Agent {
    return JSON.parse(JSON.stringify(agent));
  }

  private cloneAgents(list: Agent[]): Agent[] {
    return list.map(agent => this.cloneAgent(agent));
  }

  private buildSchedulePreview(agent: Agent | null): string {
    const t = this.transloco;

    if (!agent) {
      return t.translate('sources.agentsTable.notCreated');
    }

    if (this.resolveAgentStatus(agent) === 'deleted') {
      return t.translate('sources.agentsTable.deletedSchedule');
    }

    if (!agent.scheduleSettings) {
      return t.translate('sources.agentsTable.manualOnly');
    }

    try {
      const jsonData = JSON.parse(JSON.parse(agent.scheduleSettings)) as {
        daily: boolean,
        weekly: boolean,
        monthly: boolean,
        hour: number,
        minute: number,
        weekday: number,
        monthday: number
      };

      const type = jsonData.daily
        ? 'day'
        : jsonData.weekly
          ? 'week'
          : jsonData.monthly
            ? 'month'
            : 'hour';

      const hh = this.formatTime(jsonData.hour);
      const mm = this.formatTime(jsonData.minute);

      switch (type) {
        case 'day':
          return `${t.translate('sources.launchText.every')} ${t.translate('sources.launchText.day')} ${t.translate('sources.launchText.at')} ${hh}:${mm}`;
        case 'week': {
          const weekdayKey = this.days[jsonData.weekday] || this.days[0];
          return `${t.translate('sources.launchText.everyWeekDay.' + weekdayKey)} ${t.translate('sources.launchText.at')} ${hh}:${mm}`;
        }
        case 'month':
          return `${t.translate('sources.launchText.every')} ${t.translate('sources.launchText.month')} ${jsonData.monthday ?? 1} ${t.translate('sources.launchText.monthDay')} ${t.translate('sources.launchText.at')} ${hh}:${mm}`;
        
        default:
          return `${t.translate('sources.launchText.every')} ${t.translate('sources.launchText.hour')} ${t.translate('sources.launchText.at')} ${mm} ${t.translate('sources.launchText.minutes')}`;
      }
    } catch (error) {
      console.warn('Ошибка парсинга scheduleSettings у агента источника', agent, error);
      return t.translate('sources.agentsTable.manualOnly');
    }
  }

  private resolveAgentStatus(agent: Agent | null): AgentStatusKey {
    if (!agent) {
      return 'missing';
    }

    if (!!agent.isDeleted) {
      return 'deleted';
    }

    return !!agent.isStopped ? 'stopped' : 'running';
  }

  private formatTime(value: number | null | undefined): string {
    const safe = typeof value === 'number' && !isNaN(value) ? value : 0;
    return safe.toString().padStart(2, '0');
  }

  private async refreshAgentsForType(agentType: string): Promise<void> {
    const agents = await this.getAgentsForType(agentType, true);
    const preserved = this.agentsTable.filter(row => row.type !== agentType);
    const rows = agents
      .filter(agent => !agent.isDeleted)
      .map(agent => this.buildAgentsTableRow(agent));
    this.agentsTable = this.sortAgentsTable([...preserved, ...rows]);
  }

  public async handleScheduleCellClick(row: AgentsTableRow, event?: Event): Promise<void> {
    event?.preventDefault();
    event?.stopPropagation();
    await this.selectAgentType(row.type, row.agentId);
  }

  trackAgentRowByType(_index: number, row: AgentsTableRow): string {
    return row.agentId ? `agent-${row.agentId}` : `type-${row.type}`;
  }

  public async runDagFromTable(row: AgentsTableRow, event?: Event): Promise<void> {
    event?.stopPropagation();
    if (!row?.dagId) {
      this.alertService.info(this.transloco.translate('sources.infoTitle'), this.transloco.translate('sources.agentsTable.missingDag'));
      return;
    }

    row.isDagLoading = true;
    this.agentsTable = [...this.agentsTable];
    this.pythonService.launchAgent({ dag_id: row.dagId }).subscribe({
      next: () => {
        this.refreshDagState(row.type, row.dagId, row.agentId);
        this.refreshDagStats(row.type, row.dagId, row.agentId);
      },
      error: () => {
        this.refreshDagState(row.type, row.dagId, row.agentId);
        this.refreshDagStats(row.type, row.dagId, row.agentId);
      }
    });
  }

  private buildAgentsTableRow(agent: Agent): AgentsTableRow {
    const agentType = agent.agentType || this.selectedAgentType;
    const dagId = agent.dagId || this.buildDagId(agentType, agent.name, agent.dataSourceId, agent.dataSourceType, agent.id);
    const fallbackName = this.transloco.translate('sources.typesAgents.' + agentType);
    const displayName = (agent.name && agent.name.trim().length > 0) ? agent.name : fallbackName;
    return {
      agentId: agent.id,
      type: agentType,
      scheduleText: this.buildSchedulePreview(agent),
      statusKey: this.resolveAgentStatus(agent),
      hasAgent: true,
      dagId,
      isDagLoading: false,
      isDagRunning: undefined,
      dagStats: undefined,
      isDagStatsLoading: false,
      name: agent.name,
      displayName,
      isManualOnly: !agent.scheduleSettings,
      isStopped: !!agent.isStopped,
      isDeleted: !!agent.isDeleted
    };
  }

  private async refreshDagState(agentType: string, dagId?: string, agentId?: number): Promise<void> {
    if (!dagId) {
      return;
    }

    const row = this.findRowById(agentId) || this.agentsTable.find(r => r.type === agentType && r.dagId === dagId);
    if (!row) {
      return;
    }

    row.isDagLoading = true;
    this.agentsTable = [...this.agentsTable];

    try {
      let request = {
        dag_id: dagId
      }
      const response = await firstValueFrom(this.pythonService.getDagState(request));
      row.isDagRunning = response.running;
    } catch (error) {
      row.isDagRunning = undefined;
    } finally {
      row.isDagLoading = false;
      this.agentsTable = [...this.agentsTable];
    }
  }

  private async refreshDagStats(agentType: string, dagId?: string, agentId?: number): Promise<void> {
    if (!dagId) {
      return;
    }

    const row = this.findRowById(agentId) || this.agentsTable.find(r => r.type === agentType && r.dagId === dagId);
    if (!row) {
      return;
    }

    row.isDagStatsLoading = true;
    this.agentsTable = [...this.agentsTable];

    try {
      let request = {
        dag_id: dagId
      }
      const response = await firstValueFrom(this.pythonService.getDagStats(request));
      row.dagStats = {
        queued: response.queued ?? 0,
        running: response.running ?? 0,
        success: response.success ?? 0,
        failed: response.failed ?? 0
      };
    } catch (error) {
      row.dagStats = undefined;
    } finally {
      row.isDagStatsLoading = false;
      this.agentsTable = [...this.agentsTable];
    }
  }

  private findRowById(agentId?: number): AgentsTableRow | undefined {
    if (typeof agentId === 'number') {
      return this.agentsTable.find(row => row.agentId === agentId);
    }
    return undefined;
  }

  private sortAgentsTable(rows: AgentsTableRow[]): AgentsTableRow[] {
    const order = this.typesAgent;
    return rows.sort((a, b) => {
      const typeDiff = order.indexOf(a.type) - order.indexOf(b.type);
      if (typeDiff !== 0) return typeDiff;
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
  }

  private buildDagId(agentType: string, name?: string, dataSourceId?: number, dataSourceType?: string, agentId?: number): string {
    const dsType = (dataSourceType || (this.isShowBI ? 'bi' : 'dwh')).toLowerCase();
    const dsId = dataSourceId ?? this.selectedSource.id;
    let slug = name ?? this.transloco.translate('sources.typesAgents.' + agentType);
    slug = slug?.replace(/\s/g, "_");
    const agentIdSuffix = agentId ? `_${agentId}` : '';
    return `dag_${agentType}_${dsType}_${dsId}_${slug}${agentIdSuffix}`;
  }

  private slugify(value: string): string {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || 'agent';
  }

  allowOnlyDigits(event: KeyboardEvent) {
    const char = event.key;
    if (!/^\d$/.test(char)) {
      event.preventDefault();
    }
  }

  normalizeHour(event: any) {
    const val = Number(event.target.value);

    if (isNaN(val)) {
      this.agent.hour = null;
      return;
    }

    if (val < 0) this.agent.hour = 0;
    else if (val > 23) this.agent.hour = 23;
    else this.agent.hour = val;
  }

  normalizeMinute(event: any) {
    const val = Number(event.target.value);

    if (isNaN(val)) {
      this.agent.minute = null;
      return;
    }

    if (val < 0) this.agent.minute = 0;
    else if (val > 59) this.agent.minute = 59;
    else this.agent.minute = val;
  }

  maskHour(event: any) {
    let val = event.target.value.replace(/\D+/g, ''); // оставляем только цифры
    val = val.substring(0, 2);                        // максимум 2 цифры

    let num = Number(val);

    if (num > 23) num = 23;
    event.target.value = num.toString();
    this.agent.hour = num;
  }

  maskMinute(event: any) {
    let val = event.target.value.replace(/\D+/g, ''); // оставляем только цифры
    val = val.substring(0, 2);                        // максимум 2 цифры

    let num = Number(val);

    if (num > 59) num = 59;
    event.target.value = num.toString();
    this.agent.minute = num;
  }


  toggleOpen() {
    this.open = !this.open;
  }

  async toggleIsAgent(isAgent: boolean) {
    this.isAgent = isAgent;
    this.isDataCatalog = false;

    if (this.isAgent) {
      await this.initializeAgentsTab();
    }
  }

  toggleDataCatalog(isDataCatalog: boolean) {
    this.isDataCatalog = isDataCatalog;
    this.isAgent = false;
    this.isAgentEditorVisible = false;
  }

  saveSource(closeAfterSave: boolean = false): void {
    
    if (this.selectedSource.dataCatalogTypeSettings && JSON.parse(this.selectedSource.dataCatalogTypeSettings) && this.selectedSource.dataCatalogTypeSettings !== '{}') {
      const raw = JSON.parse((JSON.parse(this.selectedSource.dataCatalogTypeSettings || '{}')));

      let cfg: any = { providers: {} };

      cfg.providers = raw.providers || {};

      const providerPayload = this.selectedDataCatalog === 'OpenMetadata'
        ? {
          isConnected: this.selectedSource.dataCatalogIsConnected,
          host: this.selectedSource.dataCatalogHost,
          port: this.selectedSource.dataCatalogPort,
          service: this.selectedSource.dataCatalogService,
          user: this.selectedSource.openMetadataUsername,
          password: this.selectedSource.openMetadataPassword
        }
        : {
          isConnected: this.selectedSource.dataCatalogIsConnected,
          host: this.selectedSource.dataCatalogHost,
          port: this.selectedSource.dataCatalogPort,
          service: this.selectedSource.dataCatalogService,
          token: this.selectedSource.dataHubToken
        };

      cfg.providers[this.selectedDataCatalog] = providerPayload;

      this.selectedSource.dataCatalogTypeSettings = JSON.stringify(cfg);
    }
    if (this.isAgent) {
      this.saveAgentSettings(true);
      return;
    }
    
    // Для источников данных (не агентов)
    if (!this.isAgent) {
      // Data Catalog или AD-авторизация: только сохранение, без проверки подключения
      if (this.isDataCatalog || this.selectedSource.isAD) {
        this.saveSourceData(closeAfterSave);
        return;
      }

      // DWH/BI: 1) Сохраняем предыдущие настройки во временную переменную (backup).
      // 2) Сохраняем текущие настройки в БД и проверяем подключение.
      // 3) При успехе — сообщение об успехе; при «Применить и выйти» — закрыть окно.
      // 4) При неудаче — откат только в БД; форма остаётся с введёнными пользователем значениями.
      this.createdOnlyForCheckId = null;
      const backup = this.isNewSource
        ? JSON.parse(JSON.stringify(this.selectedSource))
        : (this.initialSourceState ? JSON.parse(JSON.stringify(this.initialSourceState)) : JSON.parse(JSON.stringify(this.selectedSource)));
      this.saveSourceData(closeAfterSave, true, backup, false);
    }
  }

  /** Заполняет dbSettings из полей формы (для BI). Для DWH не меняет. */
  private ensureDbSettings(): void {
    if (!this.isShowBI) return;
    if (this.selectedSource.dbType === 'Power BI') {
      this.selectedSource.dbSettings = JSON.stringify({
        server: this.selectedSource.dbServer || '',
        port: this.selectedSource.dbPort || '',
        database: this.selectedSource.dbDatabase || '',
        uid: this.selectedSource.dbUser || '',
        password: this.selectedSource.dbPassword || '',
        base_url: this.selectedSource.baseUrl || '',
        site_usr: this.selectedSource.siteUser || '',
        site_pwd: this.selectedSource.sitePassword || '',
        use_ad: this.selectedSource.isAD || false,
      });
    } else if (this.selectedSource.dbType === 'DeltaBI') {
      this.selectedSource.dbSettings = JSON.stringify({
        db_server: this.selectedSource.dbServer,
        db_port: this.selectedSource.dbPort,
        database: this.selectedSource.dbDatabase,
        db_username: this.selectedSource.dbUser,
        db_password: this.selectedSource.dbPassword,
        site_url: this.selectedSource.siteUrl,
        site_port: this.selectedSource.sitePort,
        username: this.selectedSource.siteUser,
        password: this.selectedSource.sitePassword,
      });
    } else {
      this.selectedSource.dbSettings = JSON.stringify({
        port: this.selectedSource.dbPort,
        base_url: this.selectedSource.dbServer,
        username: this.selectedSource.dbUser,
        password: this.selectedSource.dbPassword,
      });
    }
  }

  /**
   * Сохраняет настройки источника в БД. При checkAfterSave затем проверяет подключение;
   * при неудаче проверки откатывает данные в БД (форма при редактировании не меняется).
   */
  private saveSourceData(closeAfterSave: boolean = false, checkAfterSave: boolean = false, backup?: any, createdOnlyForCheck: boolean = false): void {
    this.loadingService.setLoading(true);
    this.ensureDbSettings();

    const onSaveSuccess = (sourceId?: number) => {
      if (checkAfterSave && backup != null) {
        if (sourceId != null) this.selectedSource.id = sourceId;
        const newSourceId = this.isNewSource ? sourceId : undefined;
        this.checkConnectionAfterSave(closeAfterSave, backup, newSourceId, createdOnlyForCheck);
        return;
      }

      this.loadingService.setLoading(false);
      this.alertService.success(
        this.transloco.translate('sources.connectionSuccess'),
        this.transloco.translate('sources.connectionSuccessful'),
        'OK'
      );
      if (closeAfterSave) {
        this.isVisible = false;
        this.isVisibleChange.emit(this.isVisible);
      }
      this.getSources.emit();
    };

    const onSaveError = () => {
      this.loadingService.setLoading(false);
      this.alertService.error(
        this.transloco.translate('sources.saveErrorTitle'),
        this.transloco.translate('sources.connectionError') || 'Не удалось сохранить источник данных',
        'OK'
      );
    };

    if (this.isShowBI) {
        if (!this.isNewSource)
          this.sourceService.editBI(this.selectedSource).subscribe({
            next: () => onSaveSuccess(this.selectedSource.id),
            error: (err) => {
              console.warn('editBI error', err);
              onSaveError();
            }
          });
        else
          this.sourceService.addBI(this.selectedSource).subscribe({
            next: (sourceId) => onSaveSuccess(sourceId),
            error: (err) => {
              console.warn('addBI error', err);
              onSaveError();
            }
          });
      }

      else {
        if (!this.isNewSource)
          this.sourceService.editDWH(this.selectedSource).subscribe({
            next: () => onSaveSuccess(this.selectedSource.id),
            error: (err) => {
              console.warn('editDWH error', err);
              onSaveError();
            }
          });
        else
          this.sourceService.addDWH(this.selectedSource).subscribe({
            next: (sourceId) => onSaveSuccess(sourceId),
            error: (err) => {
              console.warn('addDWH error', err);
              onSaveError();
            }
          });

      }
  }

  /**
   * Проверяет подключение после сохранения.
   * — При успехе: сообщение об успехе; при closeAfterSave — закрыть окно; обновить initialSourceState.
   * — При неудаче: откат в БД к backup; форма не меняется (пользователь видит введённые им настройки);
   *   окно остаётся открытым, показывается сообщение об ошибке.
   * Для нового источника (newSourceId): при неудаче удаляем созданный источник и восстанавливаем форму из backup.
   */
  private checkConnectionAfterSave(closeAfterSave: boolean, backup: any, newSourceId?: number, createdOnlyForCheck: boolean = false): void {
    this.loadingService.setLoading(true);

    const request: { id: number; dag_id?: string; dag_run_id?: string } = {
      id: this.selectedSource.id
    };
    if (this.selectedSource.dagId) request.dag_id = this.selectedSource.dagId;

    const checkConnection = this.isShowBI
      ? this.pythonService.checkConnectionBI(request)
      : this.pythonService.checkConnectionDWH(request);

    const revertAndShowError = (errorMessage: string) => {
      if (newSourceId != null) {
        Object.assign(this.selectedSource, backup);
        const deleteReq = { dataSourceId: newSourceId };
        if (this.isShowBI) {
          this.sourceService.deleteBI(deleteReq).subscribe({
            next: () => this.finishRevertAndShowError(errorMessage),
            error: (err) => {
              console.warn('deleteBI after failed check', err);
              this.finishRevertAndShowError(errorMessage);
            }
          });
        } else {
          this.sourceService.deleteDWH(deleteReq).subscribe({
            next: () => this.finishRevertAndShowError(errorMessage),
            error: (err) => {
              console.warn('deleteDWH after failed check', err);
              this.finishRevertAndShowError(errorMessage);
            }
          });
        }
      } else {
        const savedFormState = JSON.parse(JSON.stringify(this.selectedSource));
        Object.assign(this.selectedSource, backup);
        this.ensureDbSettings();
        const edit$ = this.isShowBI
          ? this.sourceService.editBI(this.selectedSource)
          : this.sourceService.editDWH(this.selectedSource);
        edit$.subscribe({
          next: () => {
            Object.assign(this.selectedSource, savedFormState);
            this.finishRevertAndShowError(errorMessage);
          },
          error: (err) => {
            console.warn('edit rollback error', err);
            Object.assign(this.selectedSource, savedFormState);
            this.finishRevertAndShowError(errorMessage);
          }
        });
      }
    };

    checkConnection.subscribe({
      next: (response) => {
        this.loadingService.setLoading(false);
        if (response?.status === 'success') {
          if (createdOnlyForCheck && newSourceId != null) {
            this.createdOnlyForCheckId = this.selectedSource.id;
          } else if (newSourceId == null) {
            this.initialSourceState = JSON.parse(JSON.stringify(this.selectedSource));
          }
          this.alertService.success(
            this.transloco.translate('sources.connectionSuccess'),
            this.transloco.translate('sources.connectionSuccessful'),
            'OK'
          );
          if (closeAfterSave) {
            this.isVisible = false;
            this.isVisibleChange.emit(this.isVisible);
          }
          this.getSources.emit();
        } else {
          revertAndShowError(
            this.transloco.translate('sources.connectionError') ||
            'Не удалось установить подключение. Проверьте введенные данные.'
          );
        }
      },
      error: (error: any) => {
        let errorMessage = this.transloco.translate('sources.connectionError') ||
          'Не удалось установить подключение. Проверьте введенные данные.';
        if (error?.error) {
          if (typeof error.error === 'object') {
            if (error.error.detail) errorMessage = error.error.detail;
            else if (error.error.message) errorMessage = error.error.message;
          } else if (typeof error.error === 'string') errorMessage = error.error;
        } else if (error?.message) errorMessage = error.message;
        revertAndShowError(errorMessage);
      }
    });
  }

  private finishRevertAndShowError(errorMessage: string): void {
    this.loadingService.setLoading(false);
    this.alertService.error(
      this.transloco.translate('sources.connectionFailed') || 'Подключение не удалось',
      errorMessage,
      'OK'
    );
  }

  saveAgentSettings(closeEditor: boolean = false) {
    if (!this.isAgentEditorVisible) {
      this.alertService.info(this.transloco.translate('sources.infoTitle'), this.transloco.translate('sources.agentsTable.selectAlert'));
      return;
    }

    if (typeof this.agent.name === 'string') {
      this.agent.name = this.agent.name.trim();
    } else {
      this.agent.name = '';
    }
    if (!this.agent.name) {
      this.agent.name = this.transloco.translate('sources.typesAgents.' + this.selectedAgentType);
    }

    const agentSettingsPayload = {
      daily: this.selectedScheduleType === 'day',
      weekly: this.selectedScheduleType === 'week',
      monthly: this.selectedScheduleType === 'month',
      hour: this.agent.hour,
      minute: this.agent.minute,
      weekday: this.days.indexOf(this.agent.weekdayName),
      monthday: this.agent.monthday
    };

    this.agent.scheduleSettings = !this.isNoSchedule ? JSON.stringify(agentSettingsPayload) : null;

    const finalize = () => {
      this.refreshAgentsForType(this.selectedAgentType).finally(() => {
        if (closeEditor) {
          this.closeAgentEditor();
        }
      });
    };

    if (this.isNewAgent) {
      this.agent.agentType = this.selectedAgentType;
      this.agent.dataSourceId = this.selectedSource.id;
      this.agent.dataSourceType = this.isShowBI ? 'report' : 'dwh';
      // DAG ID будет построен после получения ID из БД
      this.agent.dagId = '';
      this.sourceService.addAgent(this.agent).subscribe((created) => {
        if (created?.id) {
          this.agent.id = created.id;
          this.selectedAgentId = created.id;

          this.agent.dagId = this.buildDagId(
            this.selectedAgentType,
            this.agent.name,
            this.agent.dataSourceId,
            this.agent.dataSourceType,
            created.id
          );
          this.agent.isDeleted = false;
          this.agent.isStopped = false;

          // Сохраняем обновленный DAG ID
          this.sourceService.editAgent(this.agent).subscribe(() => finalize());
        } else {
          finalize();
        }
      });
    } else {
      if (!this.agent.dagId) {
        this.agent.dagId = this.buildDagId(
          this.selectedAgentType,
          this.agent.name,
          this.agent.dataSourceId ?? this.selectedSource.id,
          this.agent.dataSourceType,
          this.agent.id
        );
      }
      this.sourceService.editAgent(this.agent).subscribe((updated) => {
        if (updated?.id) {
          this.agent.id = updated.id;
          this.selectedAgentId = updated.id;
        }
        finalize();
      });
    }
  }

  closeAgentEditor() {
    this.isAgentEditorVisible = false;
    this.selectedAgentId = null;
    }

  openDriverDropdown = false;
  openTypeDropdown = false;

  toggleDriverDropdown() {
    this.openDriverDropdown = !this.openDriverDropdown;
  }

  toggleTypeDropdown() {
    this.openTypeDropdown = !this.openTypeDropdown;
  }

  selectDriver(driverName: string) {
    this.selectedSource.dbDriver = driverName;
    this.openDriverDropdown = false;
  }

  selectType(typeName: string) {
    this.selectedSource.dbType = typeName;
    this.openTypeDropdown = false;
  }

  selectIntervalType(interval: string) {
    this.selectedScheduleType = interval;
    this.isNoSchedule = false;

    if (!this.agent.minute)
      this.agent.minute = 0;

    if (!this.agent.hour)
      this.agent.hour = 0;

    if (!this.agent.monthday)
      this.agent.monthday = 1;

    if (!this.agent.weekdayName)
      this.agent.weekdayName = this.days[0];
    
  }

  async selectAgentType(typeAgent: string, agentId?: number, showEditor: boolean = true, forceRefresh: boolean = false) {
    this.selectedAgentType = typeAgent;
    this.selectedAgentId = typeof agentId === 'number' ? agentId : null;

    if (showEditor) {
      this.isAgentEditorVisible = true;
    }

    const agentData = await this.getAgentById(typeAgent, agentId, forceRefresh);
    this.applyAgentState(agentData);
  }



  private applyAgentState(agentData: Agent | null): void {
    if (!agentData) {
        this.isNewAgent = true;
      this.isNoSchedule = true;
      this.selectedScheduleType = 'hour';
      this.selectedAgentId = null;
      this.agent = {
        hour: 0,
        minute: 0,
        weekdayName: this.days[0],
        monthday: 1,
        dagId: '',
        scheduleSettings: null,
        isStopped: 1,
        isDeleted: 0,
        userDateTimeStr: '',
        userDateTime: null,
        name: this.transloco.translate('sources.typesAgents.' + this.selectedAgentType)
      };
      return;
    }

    this.isNewAgent = false;
    this.selectedAgentId = agentData.id ?? null;
    this.agent = {
      ...agentData,
      hour: 0,
      minute: 0,
      weekdayName: this.days[0],
      monthday: 1,
      userDateTimeStr: agentData.userDateTime?.toString(),
      userDateTime: agentData.userDateTime,
      name: agentData.name || this.transloco.translate('sources.typesAgents.' + this.selectedAgentType)
    };

    if (this.agent.scheduleSettings != null) {
      try {
        const jsonData = JSON.parse(JSON.parse(this.agent.scheduleSettings)) as {
          daily: boolean,
          weekly: boolean,
          monthly: boolean,
          hour: number,
          minute: number,
          weekday: number,
          monthday: number
        };

        this.selectedScheduleType = jsonData.daily ? 'day' :
          jsonData.weekly ? 'week' :
            jsonData.monthly ? 'month' : 'hour';

        this.isNoSchedule = false;

        this.agent.minute = jsonData.minute ?? 0;
        this.agent.hour = jsonData.hour ?? 0;

        switch (this.selectedScheduleType) {
          case 'week':
            this.agent.weekdayName = this.days[jsonData.weekday] ?? this.days[0];
            break;

          case 'month':
            this.agent.monthday = jsonData.monthday ?? 1;
            break;
        }

      } catch (e) {
        console.warn('Ошибка парсинга scheduleSettings у агента источника', this.selectedSource, e);
        this.isNoSchedule = true;
      }
    }
    else {
      this.isNoSchedule = true;
    }

  }

  launchAgent() {
    let request = {
      id: this.agent.id,
      changeMode: !this.agent.isStopped
    }

    this.sourceService.stopAgent(request).subscribe(() => {
      this.agent.isStopped = !this.agent.isStopped;
      this.refreshAgentsForType(this.selectedAgentType);
    });
  }

  deleteAgent() {
    let request = {
      id: this.agent.id,
      changeMode: !this.agent.isDeleted
    }

    this.sourceService.deleteAgent(request).subscribe(() => {
      this.agent.isDeleted = !this.agent.isDeleted;
      this.refreshAgentsForType(this.selectedAgentType).finally(() => this.closeAgentEditor());
    })
  }

  /**
   * Кнопка «Проверить подключение»:
   * 1) Сохраняем старые настройки во временную переменную (откат потом).
   * 2) Временно сохраняем новые настройки (из формы) в БД.
   * 3) Проверяем подключение с новыми настройками (индикатор загрузки).
   * 4) Показываем результат: успех или «Не удалось подключиться. Проверьте настройки».
   * 5) Независимо от результата — откатываем БД к старым настройкам; поля формы не меняем.
   */
  checkConnection(): void {
    if (this.isDataCatalog || this.isAgent) {
      return;
    }

    if (!this.selectedSource.id) {
      this.checkConnectionForNewSource();
      return;
    }

    this.checkConnectionForExistingSource();
  }

  /**
   * Проверка подключения для нового источника (без id):
   * временно добавляем в БД → проверяем → откат (удаляем). Форма не меняется.
   */
  private checkConnectionForNewSource(): void {
    this.ensureDbSettings();

    const rollbackAndFinish = (success: boolean, errorMessage?: string) => {
      const idToDelete = this.selectedSource.id;
      if (idToDelete != null) {
        const deleteReq = { dataSourceId: idToDelete };
        const delete$ = this.isShowBI ? this.sourceService.deleteBI(deleteReq) : this.sourceService.deleteDWH(deleteReq);
        delete$.subscribe({
          next: () => {
            this.selectedSource.id = undefined;
            this.loadingService.setLoading(false);
            if (success) {
              this.alertService.success(
              this.transloco.translate('sources.connectionSuccessTitle'),
              this.transloco.translate('sources.connectionSuccessful') || 'Подключение успешно установлено',
              'OK'
            );
            } else {
              this.alertService.error(
                this.transloco.translate('sources.connectionFailed') || 'Не удалось подключиться',
                errorMessage || this.transloco.translate('sources.connectionError') || 'Проверьте настройки',
                'OK'
              );
            }
          },
          error: (err) => {
            console.warn('Откат после проверки (delete) не удался', err);
            this.selectedSource.id = undefined;
            this.loadingService.setLoading(false);
            this.alertService.error(
              this.transloco.translate('sources.connectionFailed') || 'Не удалось подключиться',
              errorMessage || this.transloco.translate('sources.connectionError') || 'Проверьте настройки',
              'OK'
            );
          }
        });
      } else {
        this.loadingService.setLoading(false);
        if (success) {
          this.alertService.success(
          this.transloco.translate('sources.connectionSuccessTitle'),
          this.transloco.translate('sources.connectionSuccessful') || 'Подключение успешно установлено',
          'OK'
        );
        } else {
          this.alertService.error(
            this.transloco.translate('sources.connectionFailed') || 'Не удалось подключиться',
            errorMessage || this.transloco.translate('sources.connectionError') || 'Проверьте настройки',
            'OK'
          );
        }
      }
    };

    const add$ = this.isShowBI ? this.sourceService.addBI(this.selectedSource) : this.sourceService.addDWH(this.selectedSource);
    this.loadingService.setLoading(true);
    add$.subscribe({
      next: (sourceId) => {
        this.selectedSource.id = sourceId;
        const request: { id: number; dag_id?: string } = { id: sourceId };
        const check$ = this.isShowBI ? this.pythonService.checkConnectionBI(request) : this.pythonService.checkConnectionDWH(request);
        check$.subscribe({
          next: (response) => {
            if (response?.status === 'success') {
              rollbackAndFinish(true);
            } else {
              rollbackAndFinish(false, this.transloco.translate('sources.connectionError') || 'Проверьте настройки');
            }
          },
          error: (error: any) => {
            const msg = this.getConnectionErrorMessage(error);
            rollbackAndFinish(false, msg);
          }
        });
      },
      error: (err) => {
        this.loadingService.setLoading(false);
        console.error('Не удалось временно сохранить источник для проверки', err);
        this.alertService.error(
        this.transloco.translate('sources.errorTitle'),
        this.transloco.translate('sources.connectionError') || 'Не удалось сохранить настройки для проверки подключения',
        'OK'
      );
      }
    });
  }

  /**
   * Проверка подключения для существующего источника:
   * сохраняем старые настройки → временно пишем новые в БД → проверяем → откат в БД. Форма не меняется.
   */
  private checkConnectionForExistingSource(): void {
    const backup = JSON.parse(JSON.stringify(this.initialSourceState || this.selectedSource));
    const savedFormState = JSON.parse(JSON.stringify(this.selectedSource));
    this.ensureDbSettings();

    const rollbackAndNotify = (success: boolean, errorMessage?: string) => {
      Object.assign(this.selectedSource, backup);
      this.ensureDbSettings();
      const edit$ = this.isShowBI ? this.sourceService.editBI(this.selectedSource) : this.sourceService.editDWH(this.selectedSource);
      edit$.subscribe({
        next: () => {
          Object.assign(this.selectedSource, savedFormState);
          this.loadingService.setLoading(false);
          if (success) {
            this.alertService.success(
              this.transloco.translate('sources.connectionSuccessTitle'),
              this.transloco.translate('sources.connectionSuccessful') || 'Подключение успешно установлено',
              'OK'
            );
          } else {
            this.alertService.error(
              this.transloco.translate('sources.connectionFailed') || 'Не удалось подключиться',
              errorMessage || this.transloco.translate('sources.connectionError') || 'Проверьте настройки',
              'OK'
            );
          }
        },
        error: (err) => {
          console.warn('Откат настроек в БД не удался', err);
          Object.assign(this.selectedSource, savedFormState);
          this.loadingService.setLoading(false);
          this.alertService.error(
            this.transloco.translate('sources.connectionFailed') || 'Не удалось подключиться',
            errorMessage || this.transloco.translate('sources.connectionError') || 'Проверьте настройки',
            'OK'
          );
        }
      });
    };

    this.loadingService.setLoading(true);
    const edit$ = this.isShowBI ? this.sourceService.editBI(this.selectedSource) : this.sourceService.editDWH(this.selectedSource);
    edit$.subscribe({
      next: () => {
        const request: { id: number;} = { id: this.selectedSource.id };
        const check$ = this.isShowBI ? this.pythonService.checkConnectionBI(request) : this.pythonService.checkConnectionDWH(request);
        check$.subscribe({
          next: (response) => {
            if (response?.status === 'success') {
              rollbackAndNotify(true);
            } else {
              rollbackAndNotify(false, this.transloco.translate('sources.connectionError') || 'Проверьте настройки');
            }
          },
          error: (error: any) => {
            const msg = this.getConnectionErrorMessage(error);
            rollbackAndNotify(false, msg);
          }
        });
      },
      error: (err) => {
        this.loadingService.setLoading(false);
        console.error('Не удалось временно сохранить настройки для проверки', err);
        this.alertService.error(
        this.transloco.translate('sources.errorTitle'),
        this.transloco.translate('sources.connectionError') || 'Не удалось сохранить настройки для проверки подключения',
        'OK'
      );
      }
    });
  }

  private getConnectionErrorMessage(error: any): string {
    let msg = this.transloco.translate('sources.connectionError') || 'Проверьте настройки';
    if (error?.error) {
      if (typeof error.error === 'object') {
        if (error.error.detail) msg = error.error.detail;
        else if (error.error.message) msg = error.error.message;
      } else if (typeof error.error === 'string') msg = error.error;
    } else if (error?.message) msg = error.message;
    return msg;
  }

  launchAgentManually() {
    const dagId = this.agent.dagId || this.buildDagId(
      this.selectedAgentType,
      this.agent.name,
      this.agent.dataSourceId ?? this.selectedSource.id,
      this.agent.dataSourceType,
      this.agent.id
    );
    let request = {
      dag_id: dagId
    }

    this.pythonService.launchAgent(request).subscribe({
      next: () => {
        this.refreshDagState(this.selectedAgentType, dagId, this.agent.id);
        this.refreshDagStats(this.selectedAgentType, dagId, this.agent.id);
      },
      error: () => {
        this.refreshDagState(this.selectedAgentType, dagId, this.agent.id);
        this.refreshDagStats(this.selectedAgentType, dagId, this.agent.id);
      }
    })
  }

  clearSelection() {
    this.agent.scheduleSettings = null
  }

  clickNoSchedule() {
    this.isNoSchedule = true;

    this.agent.scheduleSettings = null
  }

  selectTypeDC(type: string): void {
    this.selectedDataCatalog = type;
    try {
      const jsonData = JSON.parse((JSON.parse(this.selectedSource.dataCatalogTypeSettings || '{}')));

      if (jsonData.providers) {
        const openMetadata = jsonData.providers.OpenMetadata;
        const dataHub = jsonData.providers.DataHub;

        if (this.selectedDataCatalog === "OpenMetadata") {
          this.selectedSource.dataCatalogIsConnected = openMetadata?.isConnected ?? false;
          this.selectedSource.dataCatalogHost = openMetadata?.host ?? "";
          this.selectedSource.dataCatalogPort = openMetadata?.port ?? "";
          this.selectedSource.dataCatalogService = openMetadata?.service ?? "";
          this.selectedSource.openMetadataPassword = openMetadata?.password ?? "";
          this.selectedSource.openMetadataUsername = openMetadata?.user ?? "";
        }
        
        if (this.selectedDataCatalog === "DataHub") {
          this.selectedSource.dataCatalogIsConnected = dataHub?.isConnected ?? false;
          this.selectedSource.dataCatalogHost = dataHub?.host ?? "";
          this.selectedSource.dataCatalogPort = dataHub?.port ?? "";
          this.selectedSource.dataCatalogService = dataHub?.service ?? ""; 
          this.selectedSource.dataHubToken = dataHub?.token ?? "";
        }
      }
    } catch (e) {
      console.error('Invalid JSON in source.dbSettings:', e);
    }
  }


  onClose() {
    if (this.createdOnlyForCheckId != null) {
      this.alertService.confirm({
        title: this.transloco.translate('sources.sourceNotSaved') || 'Источник не сохранен',
        message: this.transloco.translate('sources.sourceNotSavedMessage') || 'Источник данных был создан для проверки подключения и не был сохранен. Удалить его?',
        confirmButtonText: 'OK',
        cancelButtonText: this.transloco.translate('dialog.no') || 'Отмена',
      }).subscribe((ok) => {
        if (ok) {
          const deleteReq = { dataSourceId: this.createdOnlyForCheckId! };
          if (this.isShowBI) {
            this.sourceService.deleteBI(deleteReq).subscribe(() => this.finishClose());
          } else {
            this.sourceService.deleteDWH(deleteReq).subscribe(() => this.finishClose());
          }
        } else {
          this.finishClose();
        }
        this.createdOnlyForCheckId = null;
      });
      return;
    }
    this.finishClose();
  }

  private finishClose(): void {
    this.isVisible = false;
    this.isDataCatalog = false;
    this.isAgentEditorVisible = false;
    this.createdOnlyForCheckId = null;
    this.initialSourceState = null;
    this.isVisibleChange.emit(this.isVisible);
    this.getSources.emit();
  }


  formatDateTimeLocal(date: Date | string | null): string {
    if (!date) return '';
    const d = (date instanceof Date) ? date : new Date(date);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => n.toString().padStart(2, '0');
    const yyyy = d.getFullYear();
    const MM = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const hh = pad(d.getHours());
    const mm = pad(d.getMinutes());
    return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
  }

  private formatDateTimeDisplay(date: Date | null): string {
    if (!date) return '';
    return this.formatDateTimeLocal(date).replace('T', ' ');
  }

  private normalizeUserDateTimeInput(value: string | null | undefined): string {
    if (!value) return '';
    return value.trim().replace('T', ' ').replace(/\s+/g, ' ');
  }

  private toIsoFromDisplay(value?: string | null): string | null {
    const normalized = this.normalizeUserDateTimeInput(value);
    if (!normalized) return null;
    const d = this.parseDateTimeLocal(normalized);
    return d ? d.toISOString() : null;
  }

  parseDateTimeLocal(value: string | null | undefined): Date | null {
    if (!value) return null;
    const normalized = value.replace(' ', 'T');
    const d = new Date(normalized);
    return isNaN(d.getTime()) ? null : d;
  }

  onFlatpickrChange(event: any) {
    if (event && event.selectedDates && event.selectedDates.length > 0) {
      const d = event.selectedDates[0] as Date;
      this.agent.userDateTime = d;
      this.agent.userDateTimeStr = this.formatDateTimeDisplay(d);
    }
  }

  onADClick() {
    this.selectedSource.isAD = !this.selectedSource.isAD;
  }
}
